/**
 * OrejaNewTaskCard - Per-field inline editing for new ClickUp task proposals.
 *
 * Title and description are clickable to edit inline. Priority/Status/Assignee
 * use existing always-inline EditableSelect/AssigneeSelect. Due date and time
 * estimate are independently clickable fields.
 *
 * @module OrejaNewTaskCard
 */

import { useMemo, useState, useRef, useEffect } from 'react';
import { Calendar, Clock, Info, X, Check } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { AssigneeSelect } from '@/components/analysis/AssigneeSelect';
import { EditableSelect } from '@/components/shared/EditableField';
import { scoreDescription, type DocQualityScore } from '@/lib/oreja/doc-quality-scorer';
import type { ProposalCardData } from '@/components/shared/ProposalCard';
import type { OrejaNewTaskProposal } from '@/lib/oreja/types';

// ---------------------------------------------------------------------------
// Types (re-exported for backward compatibility)
// ---------------------------------------------------------------------------

export interface SelectOption {
  value: string;
  label: string;
  className?: string;
  color?: string;
}

export interface OrejaNewTaskCardProps {
  /** The new task proposal. */
  proposal: OrejaNewTaskProposal;
  /** Index in the proposals array. */
  index: number;
  /** Whether the card is selected for commit. */
  selected: boolean;
  /** Available priority options (from useAnalysisOptions). */
  priorityOptions: SelectOption[];
  /** Available status options (from useAnalysisOptions). */
  statusOptions: SelectOption[];
  /** Callback to toggle selection. */
  onToggle: (index: number, selected: boolean) => void;
  /** Callback to edit a field value. */
  onEdit: (index: number, field: string, value: string) => void;
  /** Callback to remove/deselect this proposal. */
  onRemove: (index: number) => void;
  /** Title of the parent task (for subtask display). */
  parentTitle?: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getDocQualityTips(score: DocQualityScore): string[] {
  const tips: string[] = [];
  if (score.description_score < 20) tips.push('Add acceptance criteria or more detail to the description');
  if (score.description_score < 30) tips.push('Include context about why this task matters');
  if (score.clarity_score < 10) tips.push('Mention current state or next steps');
  if (tips.length === 0) tips.push('Good documentation quality!');
  return tips;
}

function getConfidenceBadge(confidence: number): { label: string; className: string } {
  if (confidence >= 0.8) return { label: 'High', className: 'bg-emerald-100 text-emerald-700' };
  if (confidence >= 0.5) return { label: 'Medium', className: 'bg-amber-100 text-amber-700' };
  return { label: 'Low', className: 'bg-red-100 text-red-700' };
}

/**
 * Maps a numeric 0-1 confidence to the ProposalCardData confidence level.
 *
 * @param confidence - AI confidence score 0-1
 * @returns ProposalCardData-compatible confidence label
 */
function toConfidenceLevel(confidence: number): ProposalCardData['confidence'] {
  if (confidence >= 0.8) return 'high';
  if (confidence >= 0.5) return 'medium';
  return 'low';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Renders a single new task proposal card with per-field inline editing.
 *
 * Title and description are click-to-edit. Priority, status, and assignee
 * use always-inline EditableSelect/AssigneeSelect. Due date and time estimate
 * are independently clickable.
 *
 * @param props - Card properties
 * @returns A new task proposal card
 */
export function OrejaNewTaskCard({
  proposal,
  index,
  selected,
  priorityOptions,
  statusOptions,
  onToggle,
  onEdit,
  onRemove,
  parentTitle,
}: OrejaNewTaskCardProps) {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [savedField, setSavedField] = useState<string | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    };
  }, []);

  const conf = getConfidenceBadge(proposal.confidence);
  const docScore = useMemo(
    () => scoreDescription(proposal.description, ''),
    [proposal.description],
  );

  /** Resolve priority value for EditableSelect. Finds "normal" by label, not index. */
  const priorityValue = useMemo(() => {
    const normalFallback = priorityOptions.find(
      (o) => o.label.toLowerCase() === 'normal',
    )?.value ?? priorityOptions[0]?.value ?? '3';
    if (!proposal.priority) return normalFallback;
    const opt = priorityOptions.find(
      (o) => o.value === proposal.priority || o.value === String(proposal.priority).toLowerCase(),
    );
    return opt?.value ?? normalFallback;
  }, [proposal.priority, priorityOptions]);

  /** Resolve status value for EditableSelect. */
  const statusValue = useMemo(() => {
    if (!proposal.suggested_status) return statusOptions[0]?.value ?? 'to do';
    const opt = statusOptions.find(
      (o) => o.value.toLowerCase() === (proposal.suggested_status ?? '').toLowerCase(),
    );
    return opt?.value ?? statusOptions[0]?.value ?? 'to do';
  }, [proposal.suggested_status, statusOptions]);

  // Build the ProposalCardData representation for type compatibility
  const _cardData: ProposalCardData = useMemo(() => ({
    id: `new-${index}`,
    title: proposal.title,
    description: proposal.description,
    isNew: true,
    checked: selected,
    priority: priorityValue,
    status: statusValue,
    assignee: proposal.suggested_assignee,
    dueDate: proposal.suggested_due_date,
    timeEstimate: proposal.time_estimate,
    confidence: toConfidenceLevel(proposal.confidence),
    docQualityScore: docScore.score,
    docQualityTips: getDocQualityTips(docScore),
  }), [index, proposal, selected, priorityValue, statusValue, docScore]);

  /** Auto-select card if unselected when user clicks to edit. */
  function ensureSelected(): void {
    if (!selected) {
      onToggle(index, true);
    }
  }

  /** Flash green checkmark on save. */
  function flashSaved(fieldKey: string): void {
    setSavedField(fieldKey);
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    savedTimerRef.current = setTimeout(() => setSavedField(null), 300);
  }

  /** Start editing a specific field. */
  function startEditing(fieldKey: string): void {
    ensureSelected();
    setEditingField(fieldKey);
  }

  /** Save a field and close editing. */
  function saveField(field: string, value: string): void {
    onEdit(index, field, value);
    setEditingField(null);
    flashSaved(field);
  }

  /** Handle key down on inline inputs. */
  function handleKeyDown(e: React.KeyboardEvent, onSave: () => void): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSave();
    } else if (e.key === 'Escape') {
      setEditingField(null);
    }
  }

  return (
    <div className={cn('rounded-lg border bg-card p-4 space-y-3 transition-opacity', !selected && 'opacity-60')}>
      {/* Header row */}
      <div className="flex items-start gap-2">
        <Checkbox
          checked={selected}
          onCheckedChange={(checked) => onToggle(index, !!checked)}
          className="mt-0.5"
        />
        <div className="flex-1 min-w-0">
          {editingField === 'title' ? (
            <Input
              value={proposal.title}
              onChange={(e) => onEdit(index, 'title', e.target.value)}
              onBlur={() => saveField('title', proposal.title)}
              onKeyDown={(e) => handleKeyDown(e, () => saveField('title', proposal.title))}
              className="h-7 text-sm"
              autoFocus
            />
          ) : (
            <div className="flex items-center gap-1">
              <span
                className="text-sm font-medium cursor-pointer hover:underline decoration-dashed underline-offset-2"
                onClick={() => startEditing('title')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') startEditing('title'); }}
                aria-label="Click to edit title"
              >
                {proposal.title}
              </span>
              {savedField === 'title' && (
                <Check className="h-3 w-3 text-emerald-500 animate-in fade-in duration-200" />
              )}
            </div>
          )}
          {proposal.parent_task_id && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
              <span className="text-muted-foreground/60">⤷</span>
              <span>subtask of <span className="font-medium">{parentTitle || proposal.parent_task_id}</span></span>
              <button
                type="button"
                className="ml-1 text-[10px] text-muted-foreground/70 hover:text-foreground underline decoration-dashed underline-offset-2 cursor-pointer"
                onClick={() => { ensureSelected(); onEdit(index, 'parent_task_id', ''); }}
                aria-label="Convert to standalone task"
              >
                Make standalone
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 min-h-[44px] min-w-[44px]"
            onClick={() => onRemove(index)}
            aria-label="Remove proposal"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Badges row -- placed right after title for immediate visibility */}
      <div className="flex items-center gap-2 ml-6">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-xs text-muted-foreground cursor-help inline-flex items-center gap-1">
                Documentation: {docScore.score}/100
                <Info className="h-3 w-3" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[240px]">
              <div className="space-y-1 text-xs">
                <p className="font-medium">Score breakdown:</p>
                <p>Description: {docScore.description_score}/40</p>
                <p>Clarity: {docScore.clarity_score}/30</p>
                <p>Comments: {docScore.comment_score}/30</p>
                {getDocQualityTips(docScore).map((tip, i) => (
                  <p key={i} className="text-muted-foreground">{'\u2022'} {tip}</p>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <span className={cn('text-xs rounded px-1.5 py-0.5', conf.className)}>
          Confidence: {conf.label}
        </span>
      </div>

      {/* Description */}
      <div className="ml-6">
        {editingField === 'description' ? (
          <Textarea
            value={proposal.description}
            onChange={(e) => onEdit(index, 'description', e.target.value)}
            onBlur={() => saveField('description', proposal.description)}
            className="text-xs min-h-[80px]"
            placeholder="Task description..."
            autoFocus
          />
        ) : (
          <div className="flex items-center gap-1">
            <div
              className="rounded border bg-muted/30 p-2 text-xs whitespace-pre-wrap max-h-32 overflow-y-auto cursor-pointer hover:border-foreground/30 flex-1"
              onClick={() => startEditing('description')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') startEditing('description'); }}
              aria-label="Click to edit description"
            >
              {proposal.description || '(no description)'}
            </div>
            {savedField === 'description' && (
              <Check className="h-3 w-3 text-emerald-500 animate-in fade-in duration-200" />
            )}
          </div>
        )}
      </div>

      {/* Metadata row - always inline-editable using existing analysis patterns */}
      <div className="ml-6 space-y-1.5">
        <div className="flex flex-wrap items-center gap-3">
          {/* Priority */}
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-muted-foreground uppercase">Priority:</span>
            <EditableSelect
              value={priorityValue}
              options={priorityOptions}
              onChange={(val) => onEdit(index, 'priority', val)}
            />
          </div>

          {/* Status */}
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-muted-foreground uppercase">Status:</span>
            <EditableSelect
              value={statusValue}
              options={statusOptions}
              onChange={(val) => onEdit(index, 'suggested_status', val)}
            />
          </div>

          {/* Assignee */}
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-muted-foreground uppercase">Assignee:</span>
            <AssigneeSelect
              value={proposal.suggested_assignee}
              onChange={(val) => onEdit(index, 'suggested_assignee', val)}
              placeholder="Unassigned"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Due Date */}
          <div className="flex items-center gap-1">
            <Calendar className="h-3 w-3 text-muted-foreground" />
            {editingField === 'suggested_due_date' ? (
              <Input
                type="date"
                value={proposal.suggested_due_date ?? ''}
                onChange={(e) => onEdit(index, 'suggested_due_date', e.target.value)}
                onBlur={() => saveField('suggested_due_date', proposal.suggested_due_date ?? '')}
                onKeyDown={(e) => handleKeyDown(e, () => saveField('suggested_due_date', proposal.suggested_due_date ?? ''))}
                className="h-6 text-xs w-32"
                autoFocus
              />
            ) : (
              <span
                className="text-xs text-muted-foreground cursor-pointer hover:opacity-70"
                onClick={() => startEditing('suggested_due_date')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') startEditing('suggested_due_date'); }}
              >
                {proposal.suggested_due_date ? `Due ${proposal.suggested_due_date}` : 'No due date'}
              </span>
            )}
            {savedField === 'suggested_due_date' && (
              <Check className="h-3 w-3 text-emerald-500 animate-in fade-in duration-200" />
            )}
          </div>

          {/* Time Estimate */}
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3 text-muted-foreground" />
            {editingField === 'time_estimate' ? (
              <Input
                value={proposal.time_estimate ?? ''}
                onChange={(e) => onEdit(index, 'time_estimate', e.target.value)}
                onBlur={() => saveField('time_estimate', proposal.time_estimate ?? '')}
                onKeyDown={(e) => handleKeyDown(e, () => saveField('time_estimate', proposal.time_estimate ?? ''))}
                className="h-6 text-xs w-20"
                placeholder="e.g. 2h"
                autoFocus
              />
            ) : (
              <span
                className="text-xs text-muted-foreground cursor-pointer hover:opacity-70"
                onClick={() => startEditing('time_estimate')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') startEditing('time_estimate'); }}
              >
                {proposal.time_estimate ? `Est. ${proposal.time_estimate}` : 'No estimate'}
              </span>
            )}
            {savedField === 'time_estimate' && (
              <Check className="h-3 w-3 text-emerald-500 animate-in fade-in duration-200" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
